import { AlertTriangle, Inbox, MessageSquareReply } from 'lucide-react'
import { Helmet } from 'react-helmet'
import { Link, useSearchParams } from 'react-router'

import { Badge } from '@/components/ui/badge'
import { Button } from '@/components/ui/button'
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card'
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select'
import { SkeletonRows } from '@/components/ui/skeleton'
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table'
import { TableCard, TableCardField, TableCardList } from '@/components/ui/table-card'
import { TableSkeletonRows } from '@/components/ui/table-skeleton'
import { customerTabHref } from '@/lib/customerTabs'
import { formatDateTime } from '@/lib/formatters'
import {
  type TelephonySmsAttached,
  type TelephonySmsDirectionFilter,
  type TelephonySmsListItem,
  type TelephonySmsVendor,
  useTelephonySms,
} from '@/models/telephonySms'
import { TelephonySearchFilters } from '../Telephony/TelephonySearchFilters'
import { readTelephonySearch, telephonySearchUpdates } from '../Telephony/telephonySearchParams'

function directionBadge(msg: TelephonySmsListItem) {
  if (msg.direction === 'inbound') {
    return (
      <Badge variant="outline" className="gap-1 border-purple-200 bg-purple-50 text-purple-700">
        <Inbox className="size-3" />
        SMS · in
      </Badge>
    )
  }
  return (
    <Badge variant="outline" className="gap-1 border-purple-200 bg-purple-50 text-purple-700">
      <MessageSquareReply className="size-3" />
      SMS · out
    </Badge>
  )
}

function messageBody(msg: TelephonySmsListItem, { truncate = true } = {}) {
  return (
    <>
      <div className={truncate ? 'truncate' : 'text-left wrap-anywhere whitespace-pre-wrap'} title={msg.body}>
        {msg.body}
      </div>
      {msg.status === 'failed' && (
        <div className="text-xs text-red-600">Failed{msg.error ? ` · ${msg.error}` : ''}</div>
      )}
    </>
  )
}

function attachedTo(msg: TelephonySmsListItem) {
  if (!msg.attachedLead && !msg.attachedDriver) {
    return <span className="text-muted-foreground text-sm italic">Unattached</span>
  }
  return (
    <div className="flex flex-col">
      {msg.attachedLead && (
        <Link
          to={`/insure/internal/sales-leads/${msg.attachedLead.id}`}
          className="text-sm text-blue-600 underline-offset-2 hover:underline"
        >
          {msg.attachedLead.name}
        </Link>
      )}
      {msg.attachedDriver && (
        <Link
          to={customerTabHref(msg.attachedDriver.orgId, 'drivers')}
          className="text-xs text-blue-600 underline-offset-2 hover:underline"
        >
          Driver · {msg.attachedDriver.name}
        </Link>
      )}
    </div>
  )
}

function vendorBadge(vendor: TelephonySmsVendor) {
  if (vendor === 'dialpad') {
    return (
      <Badge variant="outline" className="border-blue-200 bg-blue-50 text-blue-700">
        Dialpad
      </Badge>
    )
  }
  return (
    <Badge variant="outline" className="border-gray-200 bg-gray-50 text-gray-700">
      Aircall
    </Badge>
  )
}

export function SmsIndex() {
  const [searchParams, setSearchParams] = useSearchParams()
  const page = Number(searchParams.get('page') || '1')
  const direction = (searchParams.get('direction') || '') as TelephonySmsDirectionFilter | ''
  const attached = (searchParams.get('attached') || '') as TelephonySmsAttached | ''
  const failedOnly = searchParams.get('failed_only') === 'true'
  const search = readTelephonySearch(searchParams)

  function updateParams(updates: Record<string, string | undefined>) {
    const params = new URLSearchParams(searchParams)
    for (const [k, v] of Object.entries(updates)) {
      if (v) params.set(k, v)
      else params.delete(k)
    }
    setSearchParams(params)
  }

  // `isLoading` is `isPending && isFetching`, so it goes false mid-retry.
  const { data, isPending, isError } = useTelephonySms({
    page,
    direction: direction || undefined,
    attached: attached || undefined,
    failedOnly,
    q: search.q || undefined,
    userId: search.userId || undefined,
    dateFrom: search.dateFrom || undefined,
    dateTo: search.dateTo || undefined,
  })
  const messages = data?.results ?? []
  const hasNext = !!data?.next
  const hasPrev = !!data?.previous

  return (
    <>
      <Helmet>
        <title>All SMS</title>
      </Helmet>
      <div className="flex flex-col gap-6 py-6 md:p-6">
        <div className="flex items-center justify-between">
          <h1 className="text-2xl font-semibold">All SMS</h1>
        </div>

        <Card>
          <CardHeader>
            <CardTitle className="text-base">Filters</CardTitle>
          </CardHeader>
          <CardContent className="flex flex-col gap-3 sm:flex-row sm:flex-wrap">
            <TelephonySearchFilters
              {...search}
              searchHint="Matches the message, either number, and the lead or customer it is on: name, DOT number, or a pasted lead or customer ID."
              onChange={(updates) => updateParams(telephonySearchUpdates(updates))}
            />
            <Select
              value={direction || 'all'}
              onValueChange={(v) => updateParams({ direction: v === 'all' ? undefined : v, page: undefined })}
            >
              <SelectTrigger className="w-full sm:w-40">
                <SelectValue placeholder="Direction" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">Any direction</SelectItem>
                <SelectItem value="inbound">Inbound only</SelectItem>
                <SelectItem value="outbound">Outbound only</SelectItem>
              </SelectContent>
            </Select>

            <Select
              value={attached || 'all'}
              onValueChange={(v) => updateParams({ attached: v === 'all' ? undefined : v, page: undefined })}
            >
              <SelectTrigger className="w-full sm:w-48">
                <SelectValue placeholder="Attached" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All messages</SelectItem>
                <SelectItem value="false">Unattached only</SelectItem>
                <SelectItem value="true">Attached only</SelectItem>
              </SelectContent>
            </Select>

            <Button
              variant={failedOnly ? 'default' : 'outline'}
              onClick={() => updateParams({ failed_only: failedOnly ? undefined : 'true', page: undefined })}
            >
              <AlertTriangle className="size-4" />
              {failedOnly ? 'Failed only ✓' : 'Failed only'}
            </Button>
          </CardContent>
        </Card>

        <Card>
          <CardContent>
            {/* Seven columns don't fit a phone, so under `lg` each message becomes a card. */}
            <div className="lg:hidden">
              {isPending && <SkeletonRows rows={6} />}
              {isError && (
                <div className="text-destructive py-6 text-center">
                  Couldn&rsquo;t load messages. Refresh to try again.
                </div>
              )}
              {!isPending && !isError && messages.length === 0 && (
                <div className="text-muted-foreground py-6 text-center">No messages match.</div>
              )}
              {!isPending && !isError && messages.length > 0 && (
                <TableCardList>
                  {messages.map((msg) => (
                    <TableCard key={msg.id} title={formatDateTime(msg.createdAt)}>
                      <TableCardField label="Direction" value={directionBadge(msg)} />
                      <TableCardField label="Vendor" value={vendorBadge(msg.vendor)} />
                      <TableCardField
                        label="From → To"
                        value={
                          <span className="font-mono text-xs">
                            {msg.fromE164} → {msg.toE164}
                          </span>
                        }
                      />
                      <TableCardField label="Message" value={messageBody(msg, { truncate: false })} />
                      <TableCardField label="Sender" value={msg.senderName} />
                      <TableCardField label="Attached to" value={attachedTo(msg)} />
                    </TableCard>
                  ))}
                </TableCardList>
              )}
            </div>
            <Table className="max-lg:hidden">
              <TableHeader>
                <TableRow>
                  <TableHead>When</TableHead>
                  <TableHead>Direction</TableHead>
                  <TableHead>Vendor</TableHead>
                  <TableHead>From → To</TableHead>
                  <TableHead>Message</TableHead>
                  <TableHead>Sender</TableHead>
                  <TableHead>Attached to</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {isPending && <TableSkeletonRows columns={7} />}
                {isError && (
                  <TableRow>
                    <TableCell colSpan={7} className="text-destructive py-6 text-center">
                      Couldn&rsquo;t load messages. Refresh to try again.
                    </TableCell>
                  </TableRow>
                )}
                {!isPending && !isError && messages.length === 0 && (
                  <TableRow>
                    <TableCell colSpan={7} className="text-muted-foreground py-6 text-center">
                      No messages match.
                    </TableCell>
                  </TableRow>
                )}
                {messages.map((msg) => (
                  <TableRow key={msg.id}>
                    <TableCell className="text-sm whitespace-nowrap">
                      {formatDateTime(msg.createdAt)}
                    </TableCell>
                    <TableCell>{directionBadge(msg)}</TableCell>
                    <TableCell>{vendorBadge(msg.vendor)}</TableCell>
                    <TableCell className="font-mono text-xs whitespace-nowrap">
                      {msg.fromE164} → {msg.toE164}
                    </TableCell>
                    <TableCell className="max-w-md text-sm">{messageBody(msg)}</TableCell>
                    <TableCell className="text-sm">
                      {msg.senderName ?? <span className="text-muted-foreground italic">—</span>}
                    </TableCell>
                    <TableCell>{attachedTo(msg)}</TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </CardContent>
        </Card>

        {(hasPrev || hasNext) && (
          <div className="flex items-center justify-between">
            <Button
              variant="outline"
              size="sm"
              disabled={!hasPrev}
              onClick={() => updateParams({ page: String(page - 1) })}
            >
              Previous
            </Button>
            <span className="text-muted-foreground text-sm">
              Page {page} · {data.count} total
            </span>
            <Button
              variant="outline"
              size="sm"
              disabled={!hasNext}
              onClick={() => updateParams({ page: String(page + 1) })}
            >
              Next
            </Button>
          </div>
        )}
      </div>
    </>
  )
}
